import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useProfiles } from "@/hooks/useProfiles";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Edit, Copy, Trash2, Settings, CheckCircle, AlertTriangle, Upload } from "lucide-react";
import { PlatformIcons } from "@/components/profiles/PlatformIcons";
import { getPayloadById } from "@/utils/payloads";
import { mockCustomers, getGroupsByCustomerId } from "@/utils/customerGroups";
import { getTagsByCustomerId } from "@/utils/customerTags";
import CustomerTag from "@/components/CustomerTag";
import { format } from "date-fns";
import { toast } from "sonner";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getIconComponent } from "@/utils/profileIcons";
import { TagsDisplay } from "@/components/tags/TagsDisplay";

export default function ProfileDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profiles, deleteProfile } = useProfiles();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const profile = profiles.find(p => p.id === id);

  if (!profile) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Profile Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The profile you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate("/profiles")}>
            Back to Profiles
          </Button>
        </div>
      </div>
    );
  }

  const getCustomerName = (id: string) => {
    return mockCustomers.find(c => c.id === id)?.name || "Unknown";
  };

  const getGroupName = (id: string) => {
    const allGroups = profile.customers?.flatMap(customerId => getGroupsByCustomerId(customerId)) || [];
    return allGroups.find(g => g.id === id)?.name || "Unknown";
  };

  const getTagName = (id: string) => {
    const allTags = profile.customers?.flatMap(customerId => getTagsByCustomerId(customerId)) || [];
    return allTags.find(t => t.id === id)?.name || "Unknown";
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      setIsDeleting(true);
      deleteProfile(profile.id);
      toast.success("Profile deleted successfully");
      navigate("/profiles");
    } catch (error) {
      toast.error("Failed to delete profile");
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleDuplicate = () => {
    // Create a copy of the profile with a new name
    const duplicatedName = `${profile.name} (Copy)`;
    // This would typically use the addProfile function
    toast.success("Profile duplicated successfully");
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b bg-card/30">
        {/* Row 1: Navigation and Actions */}
        <div className="flex items-center justify-between px-6 py-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate("/profiles")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Profiles
          </Button>
          
          <ActionsDropdown
            align="end"
            actions={[
              {
                label: "Edit",
                icon: Edit,
                onClick: () => navigate(`/profiles/${profile.id}/edit`),
              },
              {
                label: "Duplicate",
                icon: Copy,
                onClick: handleDuplicate,
              },
              {
                label: "Deploy",
                icon: Upload,
                onClick: () => toast.success("Profile deployed successfully"),
              },
              {
                label: "Delete",
                icon: Trash2,
                onClick: handleDelete,
                destructive: true,
              },
            ]}
          />
        </div>

        {/* Row 2: Profile Details */}
        <div className="px-6 pb-8 pt-4">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center shadow-sm">
              {(() => {
                const ProfileIcon = profile.icon ? getIconComponent(profile.icon) : Settings;
                return <ProfileIcon className="h-8 w-8 text-primary" />;
              })()}
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-semibold">{profile.name}</h1>
              {profile.description && (
                <p className="text-sm text-muted-foreground mt-1">{profile.description}</p>
              )}
              <div className="flex items-center space-x-3 mt-3">
                {profile.isDraft && (
                  <Badge variant="outline">Draft</Badge>
                )}
                <PlatformIcons platforms={profile.platforms} size="sm" />
                <span className="text-xs text-muted-foreground">
                  Updated {format(new Date(profile.updatedAt), "MMM d, yyyy")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Banner */}
      <div className={`border-b px-6 py-3 ${profile.isDraft ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-950 dark:border-yellow-800' : 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800'}`}>
        <div className="flex items-center gap-3">
          {profile.isDraft ? (
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
          ) : (
            <CheckCircle className="h-5 w-5 text-green-600" />
          )}
          <div>
            <p className="font-medium">
              {profile.isDraft ? "Draft Profile" : "Deployed"}
            </p>
            <p className="text-sm text-muted-foreground">
              {profile.isDraft 
                ? "This profile is saved as a draft and not yet deployed"
                : `Deployed to ${(profile.groups?.length || 0) + (profile.tags?.length || 0)} targets`
              }
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6">
              {/* General Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">General Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Name</label>
                    <p className="font-medium">{profile.name}</p>
                  </div>
                  
                  {profile.description && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Description</label>
                      <p className="text-sm">{profile.description}</p>
                    </div>
                  )}
                  
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Supported Platforms</label>
                    <div className="mt-1">
                      <PlatformIcons platforms={profile.platforms} />
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6 pt-2 border-t">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Created</label>
                      <p className="text-sm">{format(new Date(profile.createdAt), "MMM d, yyyy")}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Last Modified</label>
                      <p className="text-sm">{format(new Date(profile.updatedAt), "MMM d, yyyy")}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payloads */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Configuration Payloads
                    <Badge variant="secondary" className="ml-auto">
                      {profile.payloads.length} configured
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    Settings and restrictions applied by this profile
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {profile.payloads.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Settings className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No payloads configured</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {profile.payloads.map((profilePayload, index) => {
                        const payload = getPayloadById(profilePayload.payloadId);
                        if (!payload) return null;
                        
                        const hasConfig = Object.keys(profilePayload.configValues).length > 0;
                        
                        return (
                          <div key={profilePayload.payloadId}>
                            <div className="flex items-center justify-between p-3 rounded-lg border">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium">{payload.name}</h4>
                                  <Badge variant="outline" className="text-xs">
                                    {payload.category}
                                  </Badge>
                                </div>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {payload.description}
                                </p>
                                {hasConfig && (
                                  <div className="mt-2">
                                    <Badge variant="default" className="text-xs">
                                      {Object.keys(profilePayload.configValues).length} setting{Object.keys(profilePayload.configValues).length !== 1 ? 's' : ''} configured
                                    </Badge>
                                  </div>
                                )}
                              </div>
                            </div>
                            {index < profile.payloads.length - 1 && <Separator className="my-2" />}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Targeting */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Targeting</CardTitle>
                  <CardDescription>
                    Assignment scope for this profile
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Customer</label>
                    <div className="mt-1">
                      {profile.customers && profile.customers.length > 0 ? (
                        <CustomerTag customer={getCustomerName(profile.customers[0])} />
                      ) : profile.customer ? (
                        <CustomerTag customer={getCustomerName(profile.customer)} />
                      ) : (
                        <p className="text-sm text-muted-foreground">None assigned</p>
                      )}
                    </div>
                  </div>
                  
                  {profile.groups && profile.groups.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Groups</label>
                      <div className="mt-1 space-y-1">
                        {profile.groups.map(groupId => (
                          <div key={groupId}>
                            <Badge variant="secondary" className="text-xs">
                              {getGroupName(groupId)}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {profile.tags && profile.tags.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Tags</label>
                      <div className="mt-1">
                        <TagsDisplay 
                          resourceId={profile.id} 
                          resourceType="profile"
                          customerIds={profile.customers}
                          assignedTagIds={profile.tags}
                          showRemoveButton={false}
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Profile</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{profile.name}"? This action cannot be undone.
              {!profile.isDraft && (
                <>
                  <br /><br />
                  <strong>This profile is currently deployed to {(profile.groups?.length || 0) + (profile.tags?.length || 0)} targets.</strong>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete Profile"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}